import cn from 'classnames'
import { Fragment, ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'

import { SMB_FY_QUARTER, SMB_QUARTERS } from '../../../configs'
import { SMB_GUIDED_WIZARD_TEXT as TEXT } from '../../../configs/guidedWizardTranslations'
import { TPlanningSmbGuidedWizard } from '../../../hooks/usePlanningSmbGuidedWizard'
import { isSmbNodeExcluded } from '../../../lib/isSmbNodeExcluded'
import { normalizeSmbPercent } from '../../../lib/smbSplitStore'
import { IPlanningSmbGuidedWizardProps } from '../../../model/guidedWizardTypes'
import { ISmbNode, TSmbSplitResult } from '../../../model/types'
import styles from '../PlanningSmbGuidedWizard.module.scss'

import { PlanningSmbGuidedControls } from './PlanningSmbGuidedControls'
import { formatGuidedPercent } from './PlanningSmbGuidedOverview'
import {
  PlanningSmbGuidedText as Text,
  TGuidedTranslationParams,
  usePlanningSmbGuidedTranslation,
} from './PlanningSmbGuidedText'

const PercentInput = ({
  value,
  custom,
  label,
  disabled,
  onCommit,
}: {
  value: TSmbSplitResult
  custom: boolean
  label: string
  disabled: boolean
  onCommit: (value: number | null) => void
}) => {
  const translate = usePlanningSmbGuidedTranslation()
  const displayed = value.isEmpty ? '' : formatGuidedPercent(value.smb)
  const [input, setInput] = useState(displayed)
  const cancelled = useRef(false)
  useEffect(() => setInput(displayed), [displayed])
  return (
    <span className={cn(styles.percentInput, { [styles.customInput]: custom })}>
      <input
        type="number"
        inputMode="decimal"
        min={0}
        max={100}
        step={0.1}
        value={input}
        aria-label={label}
        disabled={disabled}
        placeholder="—"
        onChange={(event) => setInput(event.target.value)}
        onBlur={() => {
          if (cancelled.current) {
            cancelled.current = false
            return
          }
          if (input === '') {
            onCommit(null)
            setInput(displayed)
            return
          }
          const numeric = Number(input)
          if (!Number.isFinite(numeric)) {
            setInput(displayed)
            return
          }
          const normalized = normalizeSmbPercent(numeric)
          setInput(formatGuidedPercent(normalized))
          if (value.isEmpty || normalized !== normalizeSmbPercent(value.smb)) onCommit(normalized)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
          if (event.key === 'Escape') {
            event.stopPropagation()
            cancelled.current = true
            setInput(displayed)
            event.currentTarget.blur()
          }
        }}
      />
      <span className={styles.percentSuffix} aria-hidden>
        %
      </span>
      {custom && (
        <button
          className={styles.reset}
          type="button"
          title={translate(TEXT.reset)}
          aria-label={`${translate(TEXT.reset)}: ${label}`}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onCommit(null)}
        >
          ×
        </button>
      )}
    </span>
  )
}

const FinancialValues = ({
  value,
  total,
  format,
}: {
  value: TSmbSplitResult
  total: number
  format: (value: number) => string
}) => (
  <span className={styles.financialValues}>
    <span className={styles.financialSmb}>
      {value.isEmpty ? '—' : format((total * value.smb) / 100)}
    </span>
    <span className={styles.financialEnterprise}>
      {value.isEmpty ? '—' : format((total * value.eu) / 100)}
    </span>
    <span>{format(total)}</span>
  </span>
)

interface IDetailsProps {
  props: IPlanningSmbGuidedWizardProps
  editor: TPlanningSmbGuidedWizard
  params: TGuidedTranslationParams
}

export const PlanningSmbGuidedDetails = ({ props, editor, params }: IDetailsProps) => {
  const { formatMoney } = props
  const translate = usePlanningSmbGuidedTranslation()
  const t = useSafetyTranslation()
  const format = useCallback(
    (value: number) =>
      `${editor.currency === 'USD' ? '$' : `${editor.currency} `}${formatMoney(value)}`,
    [editor.currency, formatMoney]
  )
  const futureYears = editor.years.slice(1)
  const columnCount = 2 + editor.years.length + (editor.showQuarters ? SMB_QUARTERS.length : 0)
  const rowSpan = editor.showQuarters ? 2 : undefined
  const adjustedCount = editor.categories.filter((category) =>
    editor.nodes.some(
      (node) =>
        node.category === category.category &&
        editor.years.some((year) =>
          [SMB_FY_QUARTER, ...(year === props.fYear ? SMB_QUARTERS : [])].some((fq) =>
            editor.isCustom(node, year, fq)
          )
        )
    )
  ).length

  const renderRow = (node: ISmbNode, depth: number): ReactNode => {
    const product = node.kind === 'offering' ? props.programType : t(node.nameKey)
    const baseline = props.getBaselineValue(node)
    const hasChildren = node.children.some((child) => !isSmbNodeExcluded(child))
    const expanded = editor.expandedNodes.has(node.id)
    const planCell = (year: number, fq = SMB_FY_QUARTER) => {
      const value = editor.getValue(node, year, fq)
      const custom = editor.isCustom(node, year, fq)
      const quarter = fq === SMB_FY_QUARTER ? '' : ` ${translate(TEXT.quarter, { quarter: fq })}`
      return (
        <td
          key={`${year}-${fq}`}
          className={cn({
            [styles.quarterCell]: fq !== SMB_FY_QUARTER,
            [styles.futureCell]: year > props.fYear,
          })}
        >
          <div className={styles.planCell}>
            <PercentInput
              value={value}
              custom={custom}
              label={translate(TEXT.percentLabel, {
                product,
                year: String(year).slice(-2),
                quarter,
              })}
              disabled={editor.isApplying}
              onCommit={(value) => editor.edit(node, year, fq, value)}
            />
            {editor.showFinancialData && (
              <FinancialValues
                value={value}
                total={editor.getFinancialAmount(node, year, fq)}
                format={format}
              />
            )}
          </div>
        </td>
      )
    }

    return (
      <Fragment key={node.id}>
        <tr
          className={cn(styles.productRow, { [styles.nestedRow]: depth > 0 })}
          data-node-id={node.id}
        >
          <th scope="row" className={styles.nameCell} style={{ paddingLeft: 14 + depth * 20 }}>
            <div className={styles.nameLayout}>
              <span className={styles.productName}>
                {node.kind !== 'offering' && hasChildren && (
                  <button
                    className={styles.productExpand}
                    type="button"
                    aria-expanded={expanded}
                    disabled={editor.isApplying}
                    aria-label={translate(expanded ? TEXT.collapseProduct : TEXT.expandProduct, {
                      product,
                    })}
                    onClick={() => editor.toggleExpanded(node)}
                  >
                    <span aria-hidden>{expanded ? '▾' : '▸'}</span>
                  </button>
                )}
                {node.kind === 'offering' ? (
                  props.programType
                ) : (
                  <Translation span text={node.nameKey} />
                )}
              </span>
              {editor.showFinancialData && (
                <span className={styles.financialLabels}>
                  <span className={styles.financialSmb}>
                    <Text text={TEXT.smb} />
                  </span>
                  <span className={styles.financialEnterprise}>
                    <Text text={TEXT.enterprise} />
                  </span>
                  <span>
                    <Text text={TEXT.total} />
                  </span>
                </span>
              )}
            </div>
          </th>
          <td className={styles.baselineCell}>
            <span className={styles.baselineValue}>
              {baseline.isEmpty ? '—' : `${formatGuidedPercent(baseline.smb)}%`}
            </span>
            {editor.showFinancialData && (
              <FinancialValues
                value={baseline}
                total={editor.getFinancialAmount(node, props.fYear - 1)}
                format={format}
              />
            )}
          </td>
          {planCell(props.fYear)}
          {editor.showQuarters && SMB_QUARTERS.map((fq) => planCell(props.fYear, fq))}
          {futureYears.map((year) => planCell(year))}
        </tr>
        {node.kind !== 'offering' &&
          expanded &&
          node.children
            .filter((child) => !isSmbNodeExcluded(child))
            .map((child) => renderRow(child, depth + 1))}
      </Fragment>
    )
  }

  return (
    <div className={styles.details}>
      <PlanningSmbGuidedControls props={props} editor={editor} />
      <div className={styles.tableViewport} tabIndex={0}>
        <table className={cn(styles.table, { [styles.tableQuarterly]: editor.showQuarters })}>
          <thead>
            <tr>
              <th rowSpan={rowSpan} className={styles.productHeading}>
                <Text text={TEXT.product} />
              </th>
              <th rowSpan={rowSpan} className={styles.baselineHeading}>
                <Text text={TEXT.fiscalYear} params={{ year: params.baselineYear }} />
                <span className={styles.headingSub}>
                  <Text text={TEXT.actual} />
                </span>
              </th>
              <th rowSpan={rowSpan} className={styles.yearHeading}>
                <Text text={TEXT.yearPlan} params={{ year: params.fYear }} />
                <span className={styles.headingSub}>
                  <Text text={TEXT.smbPercent} />
                </span>
                {!editor.showQuarters && (
                  <button
                    type="button"
                    className={styles.quartersToggle}
                    disabled={editor.isApplying}
                    aria-expanded={false}
                    onClick={() => editor.setShowQuarters(true)}
                  >
                    <Text text={TEXT.quarters} /> ▸
                  </button>
                )}
              </th>
              {editor.showQuarters && (
                <th colSpan={4} className={styles.quarterGroup}>
                  <Text text={TEXT.byQuarter} params={params} />
                  <span className={styles.optionalTag}>
                    <Text text={TEXT.optional} />
                  </span>
                  <button
                    type="button"
                    className={styles.quartersToggle}
                    aria-expanded
                    disabled={editor.isApplying || props.isQuarterlyPlanningDisabled}
                    onClick={() => editor.setShowQuarters(false)}
                  >
                    <Text text={TEXT.collapse} />
                  </button>
                </th>
              )}
              {futureYears.map((year) => (
                <th key={year} rowSpan={rowSpan} className={styles.yearHeading}>
                  <Text text={TEXT.yearPlan} params={{ year: String(year).slice(-2) }} />
                  <span className={styles.headingSub}>
                    <Text text={TEXT.smbPercent} />
                  </span>
                </th>
              ))}
            </tr>
            {editor.showQuarters && (
              <tr>
                {SMB_QUARTERS.map((quarter) => (
                  <th className={styles.quarterHeading} key={quarter}>
                    <Text text={TEXT.quarter} params={{ quarter }} />
                  </th>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {renderRow(props.tree, 0)}
            {editor.categories.length > 0 && (
              <tr className={styles.categoriesHeading}>
                <td colSpan={columnCount}>
                  <button
                    type="button"
                    className={styles.categoriesToggle}
                    aria-expanded={editor.showCategories}
                    disabled={editor.isApplying}
                    onClick={() => editor.setShowCategories(!editor.showCategories)}
                  >
                    <span className={styles.caret} aria-hidden>
                      {editor.showCategories ? '▾' : '▸'}
                    </span>
                    <span className={styles.categoryCount}>
                      <Text
                        text={TEXT.subCategories}
                        params={{ count: editor.categories.length }}
                      />
                    </span>
                    <span className={styles.optionalTag}>
                      <Text text={TEXT.optional} />
                    </span>
                    <span className={styles.categoryStatus}>
                      <Text
                        text={adjustedCount ? TEXT.adjusted : TEXT.follows}
                        params={{
                          ...params,
                          adjusted: adjustedCount,
                          following: editor.categories.length - adjustedCount,
                        }}
                      />
                    </span>
                    <span className={styles.categoryAction}>
                      <Text text={editor.showCategories ? TEXT.hide : TEXT.adjust} />
                    </span>
                  </button>
                </td>
              </tr>
            )}
            {editor.showCategories && (
              <>
                <tr className={styles.categoryNote}>
                  <td colSpan={columnCount}>
                    <span>
                      <i className={styles.inheritedSwatch} aria-hidden />
                      <Text text={TEXT.inheritedLegend} params={params} />
                    </span>
                    <span>
                      <i className={styles.customSwatch} aria-hidden />
                      <Text text={TEXT.customLegend} />
                    </span>
                    <span>
                      <Text text={TEXT.detailNote} />
                    </span>
                  </td>
                </tr>
                {editor.categories.map((node) => renderRow(node, 1))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
